/**
 * Closed beta: remembers that the account is not (yet) approved by an admin.
 *
 * Set when the API answers 403 ACCOUNT_PENDING_APPROVAL / ACCOUNT_REJECTED
 * (apiClient — login or any request) or when the sign-up verification says the
 * new account waits for approval (RegisterPage). AuthProvider subscribes and
 * signs the user out; ProtectedRoute and the login page then lead to
 * /account-pending (AccountPendingPage), which explains the state. The flag
 * lasts for the page session and is cleared by the next successful login.
 */
type Listener = () => void;

export type AccountApprovalState = 'pending' | 'rejected';

export const ACCOUNT_PENDING_APPROVAL_CODE = 'ACCOUNT_PENDING_APPROVAL';
export const ACCOUNT_REJECTED_CODE = 'ACCOUNT_REJECTED';

/** Route of the "waiting for approval" page. */
export const ACCOUNT_PENDING_PATH = '/account-pending';

let state: AccountApprovalState | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** The approval state an API error code stands for, or null for any other code. */
export function approvalStateOfCode(code: string | undefined): AccountApprovalState | null {
  if (code === ACCOUNT_PENDING_APPROVAL_CODE) return 'pending';
  if (code === ACCOUNT_REJECTED_CODE) return 'rejected';
  return null;
}

export function markAccountApproval(next: AccountApprovalState): void {
  if (state === next) return;
  state = next;
  emit();
}

export function getAccountApproval(): AccountApprovalState | null {
  return state;
}

/** Called after a successful login. */
export function clearAccountApproval(): void {
  if (state === null) return;
  state = null;
  emit();
}

export function subscribeAccountApproval(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
